import axios from "axios";
import {
  SocialConnection,
  SocialEntitySyncInput,
  SocialPlatform,
  SocialSyncJob,
  SocialSyncPolicy,
} from "../types/social.types";

type ApiResponse<T> = {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
};

type SocialAuthContext = {
  userId?: string;
  token?: string;
};

type SocialAuthProvider = () => Promise<SocialAuthContext> | SocialAuthContext;

const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_URL ||
  "http://localhost:5000";

const api = axios.create({
  baseURL: API_BASE_URL,
});

let authProvider: SocialAuthProvider | null = null;

export const setSocialAuthProvider = (provider: SocialAuthProvider) => {
  authProvider = provider;
};

const getStoredUserId = (): string => {
  const possibleKeys = [
    "uid",
    "userId",
    "firebaseUserId",
    "currentUserId",
    "sellerId",
  ];

  for (const key of possibleKeys) {
    const value = localStorage.getItem(key);

    if (value) return value;
  }

  try {
    const rawUser =
      localStorage.getItem("user") ||
      localStorage.getItem("currentUser") ||
      localStorage.getItem("authUser");

    if (rawUser) {
      const parsed = JSON.parse(rawUser);

      if (parsed?.uid) return parsed.uid;
      if (parsed?.id) return parsed.id;
      if (parsed?._id) return parsed._id;
      if (parsed?.userId) return parsed.userId;
    }
  } catch {
    // Ignore localStorage JSON issues.
  }

  return "system";
};

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const context = authProvider ? await authProvider() : {};
  const userId = context.userId || getStoredUserId();

  const headers: Record<string, string> = {
    "x-user-id": userId,
  };

  if (context.token) {
    headers.Authorization = `Bearer ${context.token}`;
  }

  return headers;
};

const unwrap = <T>(response: { data: ApiResponse<T> }): T => {
  if (!response.data.success) {
    throw new Error(
      response.data.message || response.data.error || "Request failed",
    );
  }

  return response.data.data as T;
};

const defaultPolicy: SocialSyncPolicy = {
  automationLevel: "manual",

  autoImport: false,
  autoEnhanceImages: true,
  autoGenerateDescriptions: true,
  autoPublishToPickDengu: false,

  publishToFacebook: false,
  publishToInstagram: false,
  publishToTikTok: false,
  publishToGoogleMerchant: false,
  publishToWhatsApp: false,

  requireApprovalBeforeSocialPublish: true,
  requireApprovalBeforePickDenguPublish: true,

  maxAutoPostsPerDay: 3,
};

export const socialApi = {
  async getConnections(): Promise<SocialConnection[]> {
    const response = await api.get<ApiResponse<SocialConnection[]>>(
      "/api/v1/social/connections",
      {
        headers: await getAuthHeaders(),
      },
    );

    return unwrap(response) || [];
  },

  getConnectUrl(platform: SocialPlatform, ownerId?: string): string {
    const uid = ownerId || getStoredUserId();

    return `${API_BASE_URL}/api/v1/social/connect/${platform}/start?ownerId=${encodeURIComponent(
      uid,
    )}`;
  },

  async updateConnectionSettings(
    connectionId: string,
    settings: Partial<SocialConnection>,
  ): Promise<SocialConnection> {
    const response = await api.patch<ApiResponse<SocialConnection>>(
      `/api/v1/social/connections/${connectionId}/settings`,
      settings,
      {
        headers: await getAuthHeaders(),
      },
    );

    return unwrap(response);
  },

  async getPolicy(): Promise<SocialSyncPolicy> {
    try {
      const response = await api.get<ApiResponse<SocialSyncPolicy>>(
        "/api/v1/social/policies",
        {
          headers: await getAuthHeaders(),
        },
      );

      return unwrap(response) || defaultPolicy;
    } catch {
      return defaultPolicy;
    }
  },

  async savePolicy(policy: SocialSyncPolicy): Promise<SocialSyncPolicy> {
    const response = await api.post<ApiResponse<SocialSyncPolicy>>(
      "/api/v1/social/policies",
      policy,
      {
        headers: await getAuthHeaders(),
      },
    );

    return unwrap(response) || policy;
  },

  async syncEntity(input: SocialEntitySyncInput): Promise<SocialSyncJob[]> {
    const response = await api.post<ApiResponse<SocialSyncJob[]>>(
      "/api/v1/social/sync/entity",
      input,
      {
        headers: await getAuthHeaders(),
      },
    );

    return unwrap(response) || [];
  },

  async getJobsForEntity(
    entityType: string,
    entityId: string,
  ): Promise<SocialSyncJob[]> {
    const response = await api.get<ApiResponse<SocialSyncJob[]>>(
      `/api/v1/social/jobs/entity/${entityType}/${entityId}`,
      {
        headers: await getAuthHeaders(),
      },
    );

    return unwrap(response) || [];
  },
};
